import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { VoteGroupService, ElectionService } from '../services/firebaseService';
import { EmailService } from '../services/emailService';
import { VoteGroup, Election } from '../types';
import CreateVoteGroupModal from '../components/CreateVoteGroupModal';
import EditVoteGroupModal from '../components/EditVoteGroupModal';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  PlayIcon,
  PauseIcon,
  Bars3Icon,
  ArrowRightIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const AdminVoteGroups: React.FC = () => {
  const navigate = useNavigate();
  const [voteGroups, setVoteGroups] = useState<VoteGroup[]>([]);
  const [elections, setElections] = useState<Election[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVoteGroup, setSelectedVoteGroup] = useState<VoteGroup | null>(null);
  const [sendingLinks, setSendingLinks] = useState(false);
  const [smtpTested, setSmtpTested] = useState(false);
  const [smtpStatus, setSmtpStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [voteGroupsData, electionsData] = await Promise.all([
          VoteGroupService.getAllVoteGroups(),
          ElectionService.getAllElections()
        ]);
        setVoteGroups(voteGroupsData);
        setElections(electionsData);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleToggleVoteGroup = async (voteGroupId: string, isActive: boolean) => {
    try {
      await VoteGroupService.updateVoteGroup(voteGroupId, { isActive: !isActive });
      setVoteGroups(prev => prev.map(vg => 
        vg.id === voteGroupId ? { ...vg, isActive: !isActive } : vg
      ));
    } catch (error) {
      console.error('Erreur lors de la mise à jour du groupe:', error);
    }
  };

  const handleDeleteVoteGroup = async (voteGroupId: string) => {
    const voteGroup = voteGroups.find(vg => vg.id === voteGroupId);
    const groupName = voteGroup?.name || 'ce groupe';
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${groupName}" ? Cette action est irréversible et supprimera définitivement le groupe d'élections.`)) {
      try {
        await VoteGroupService.deleteVoteGroup(voteGroupId);
        setVoteGroups(prev => prev.filter(vg => vg.id !== voteGroupId));
        setError(''); // Clear any previous errors
      } catch (error) {
        console.error('Erreur lors de la suppression du groupe:', error);
        setError('Erreur lors de la suppression du groupe');
      }
    }
  };

  const handleCreateSuccess = () => {
    // Recharger les groupes d'élections après création
    const loadVoteGroups = async () => {
      try {
        const voteGroupsData = await VoteGroupService.getAllVoteGroups();
        setVoteGroups(voteGroupsData);
      } catch (error) {
        console.error('Erreur lors du rechargement des groupes:', error);
      }
    };
    loadVoteGroups();
  };

  const handleEditVoteGroup = (voteGroup: VoteGroup) => {
    setSelectedVoteGroup(voteGroup);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    // Recharger les groupes d'élections après modification
    const loadVoteGroups = async () => {
      try {
        const voteGroupsData = await VoteGroupService.getAllVoteGroups();
        setVoteGroups(voteGroupsData);
      } catch (error) {
        console.error('Erreur lors du rechargement des groupes:', error);
      }
    };
    loadVoteGroups();
  };

  const handleTestVoteGroup = (voteGroup: VoteGroup) => {
    navigate(`/vote/group/${voteGroup.id}`);
  };

  const handleTestSMTP = async () => {
    setSmtpStatus('testing');
    try {
      const isConnected = await EmailService.testSMTPConnection();
      if (isConnected) {
        setSmtpStatus('success');
        setSmtpTested(true);
        setError('');
      } else {
        setSmtpStatus('error');
        setError('Connexion SMTP échouée. Vérifiez vos paramètres.');
      }
    } catch (error) {
      setSmtpStatus('error');
      setError('Erreur de test SMTP. Vérifiez votre configuration.');
    }
  };

  const handleSendGroupMagicLinks = async (voteGroup: VoteGroup) => {
    if (!smtpTested) {
      setError('Veuillez d\'abord tester la connexion SMTP');
      return;
    }

    if (window.confirm(`Envoyer les liens magiques pour le groupe "${voteGroup.name}" à tous les membres actifs ?`)) {
      setSendingLinks(true);
      
      try {
        // Simuler l'envoi des liens magiques
        // Dans une vraie implémentation, on récupérerait les membres depuis la base
        const mockMembers = [
          { id: '1', email: 'grace.monzele@asprocom.com', firstName: 'Grace', lastName: 'Monzele' },
          { id: '2', email: 'jean.doe@asprocom.com', firstName: 'Jean', lastName: 'Doe' },
          { id: '3', email: 'marie.smith@asprocom.com', firstName: 'Marie', lastName: 'Smith' }
        ];
        
        // Envoyer les liens magiques via SendGrid
        const result = await EmailService.sendGroupMagicLinks(
          voteGroup.id,
          voteGroup.name,
          mockMembers
        );
        
        console.log(`✅ ${result.success} emails envoyés avec succès via Firebase Functions`);
        if (result.errors.length > 0) {
          console.error('❌ Erreurs:', result.errors);
          setError(`${result.errors.length} erreurs lors de l'envoi`);
        } else {
          setError('');
        }
        
        // Mettre à jour le statut du groupe
        await VoteGroupService.updateVoteGroup(voteGroup.id, { emailSent: true });
        
        // Recharger les groupes
        const updatedGroups = await VoteGroupService.getAllVoteGroups();
        setVoteGroups(updatedGroups);
        
        setError('');
        
      } catch (error) {
        console.error('Erreur lors de l\'envoi des liens:', error);
        setError('Erreur lors de l\'envoi des liens magiques');
      } finally {
        setSendingLinks(false);
      }
    }
  };

  const getElectionById = (electionId: string) => {
    return elections.find(e => e.id === electionId);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Groupes d'Élections</h1>
          <p className="text-gray-600">Gérez les groupes d'élections pour le vote groupé</p>
        </div>
        <div className="flex items-center space-x-4">
          {/* Test SMTP */}
          <button
            onClick={handleTestSMTP}
            disabled={smtpStatus === 'testing'}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors duration-200 ${
              smtpStatus === 'success' 
                ? 'bg-green-600 text-white hover:bg-green-700'
                : smtpStatus === 'error'
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {smtpStatus === 'testing' ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Test...
              </>
            ) : smtpStatus === 'success' ? (
              <>
                <CheckCircleIcon className="w-4 h-4 mr-2" />
                SMTP OK
              </>
            ) : smtpStatus === 'error' ? (
              <>
                <XCircleIcon className="w-4 h-4 mr-2" />
                SMTP Error
              </>
            ) : (
              <>
                <EnvelopeIcon className="w-4 h-4 mr-2" />
                Tester SMTP
              </>
            )}
          </button>
          
          <button 
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Créer un groupe
          </button>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Liste des groupes d'élections */}
      {voteGroups.length === 0 ? (
        <div className="text-center py-12">
          <Bars3Icon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun groupe d'élections</h3>
          <p className="text-gray-600 mb-4">Créez votre premier groupe d'élections pour organiser le vote.</p>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            Créer un groupe
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {voteGroups.map((voteGroup) => (
            <div key={voteGroup.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{voteGroup.name}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      voteGroup.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {voteGroup.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 mb-4">{voteGroup.description}</p>
                  
                  {/* Période de vote */}
                  <div className="text-sm text-gray-500 mb-4">
                    <span className="font-medium">Période :</span> {formatDate(voteGroup.startDate)} - {formatDate(voteGroup.endDate)}
                  </div>

                  {/* Élections du groupe */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Élections ({voteGroup.elections.length})</h4>
                    <div className="space-y-2">
                      {voteGroup.elections.map((electionId, index) => {
                        const election = getElectionById(electionId);
                        return election ? (
                          <div key={electionId} className="flex items-center space-x-3 text-sm">
                            <span className="w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-medium">
                              {index + 1}
                            </span>
                            <span className="text-gray-900">{election.title}</span>
                            <span className="text-gray-500">({election.position})</span>
                          </div>
                        ) : (
                          <div key={electionId} className="text-sm text-red-600">
                            Élection supprimée (ID: {electionId})
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Statut d'envoi d'emails */}
                  <div className="flex items-center space-x-4 text-sm">
                    <span className={`flex items-center ${
                      voteGroup.emailSent ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      <div className={`w-2 h-2 rounded-full mr-2 ${
                        voteGroup.emailSent ? 'bg-green-500' : 'bg-gray-400'
                      }`}></div>
                      {voteGroup.emailSent ? 'Emails envoyés' : 'Emails non envoyés'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 ml-4">
                  <button 
                    onClick={() => handleToggleVoteGroup(voteGroup.id, voteGroup.isActive)}
                    className={`p-2 rounded-md ${
                      voteGroup.isActive 
                        ? 'text-orange-600 hover:bg-orange-50' 
                        : 'text-green-600 hover:bg-green-50'
                    }`}
                    title={voteGroup.isActive ? 'Désactiver le groupe' : 'Activer le groupe'}
                  >
                    {voteGroup.isActive ? (
                      <PauseIcon className="w-5 h-5" />
                    ) : (
                      <PlayIcon className="w-5 h-5" />
                    )}
                  </button>
                  
                  <button 
                    onClick={() => handleSendGroupMagicLinks(voteGroup)}
                    disabled={sendingLinks}
                    className="p-2 text-green-600 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                    title="Envoyer les liens magiques"
                  >
                    <EnvelopeIcon className="w-5 h-5" />
                  </button>
                  
                  <button 
                    onClick={() => handleTestVoteGroup(voteGroup)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                    title="Tester le vote groupé"
                  >
                    <ArrowRightIcon className="w-5 h-5" />
                  </button>
                  
                  <button 
                    onClick={() => handleEditVoteGroup(voteGroup)}
                    className="p-2 text-gray-600 hover:bg-gray-50 rounded-md"
                    title="Modifier le groupe"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  
                  <button 
                    onClick={() => handleDeleteVoteGroup(voteGroup.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                    title="Supprimer le groupe"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de création de groupe */}
      <CreateVoteGroupModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Modal d'édition de groupe */}
      <EditVoteGroupModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedVoteGroup(null);
        }}
        onSuccess={handleEditSuccess}
        voteGroup={selectedVoteGroup}
      />
    </div>
  );
};

export default AdminVoteGroups;
